import { BaseApiService } from "../base/base-api.service";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CustomErrorHandlerService } from '../custom-error-handler.service';
import { PreferenceDashboard } from '../../../models/preference-dashboard/preferente-dashboard.model';
import { PreferenceDashboardSerializer } from "../../../models/preference-dashboard/preference-dashboard-serializer.class";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CorePreference, ExpiringPermissionsWidget, PermissionsByRegulationItem, ContactIngestionSummary, ContactSubmissionsBySource } from "@fourcomply-admin/shared";

@Injectable()
export class PreferenceDashboardService extends BaseApiService<PreferenceDashboard> {
    constructor(httpClient: HttpClient) {
        super(
            httpClient, 
            null, 
            "preferencedashboard",
            new PreferenceDashboardSerializer(), 
            new CustomErrorHandlerService()
        );
    }

    getActivePages(businessUnit: string): Observable<string> {
        return this.httpClient.get<string>(`${this.url}/preferencedashboard/getactivepreferencepages`, {
            params: { businessUnit }
        });
    }

    getPageSubmissions(businessUnit: string, from?: string, to?: string): Observable<string> {
        const params: { [key: string]: string } = { businessUnit };
        if (from) {
            params['from'] = from;
        }
        if (to) {
            params['to'] = to;
        }
        return this.httpClient.get(`${this.url}/preferencedashboard/getpagesubmissions`, {
            params,
            responseType: 'text'
        });
    }

    getContactSubmissions(businessUnit: string, from?: string, to?: string): Observable<string> {
        const params: { [key: string]: string } = { businessUnit };
        if (from) {
            params['from'] = from;
        }
        if (to) {
            params['to'] = to;
        }
        return this.httpClient.get<string>(`${this.url}/preferencedashboard/getcontactsubmissions`, {
            params
        });
    }

    getMostSubmittedCorePreference(businessUnit: string, dateRange: string): Observable<PreferenceDashboard[]> {
        return this.httpClient.get<PreferenceDashboard[]>(`${this.url}/preferencedashboard/getmostsubmittedcorepreference`, {
            params: { businessUnit, dateRange }
        });
    }
    
    getMostSubmittedPage(businessUnit: string, dateRange: string, from?: string, to?: string): Observable<PreferenceDashboard[]> {
        const params: { [key: string]: string } = { businessUnit, dateRange };
        if (from && to) {
            params['from'] = from;
            params['to'] = to;
        }
        return this.httpClient.get<PreferenceDashboard[]>(`${this.url}/preferencedashboard/getmostsubmittedpage`, {
            params
        });
    }

    getContactSubmissionsBySource(businessUnit: string, from?: string, to?: string): Observable<ContactSubmissionsBySource> {
        const params: { [key: string]: string } = { businessUnit };
        if (from) {
            params['from'] = from;
        }
        if (to) {
            params['to'] = to;
        }
        return this.httpClient.get<ContactSubmissionsBySource>(
            `${this.url}/preferencedashboard/getcontactsubmissionsbysource`,
            { params }
        );
    }

    getExpiringPermissionsWidget(): Observable<ExpiringPermissionsWidget> {
        return this.httpClient.get<ExpiringPermissionsWidget>(`${this.url}/preferencedashboard/expiringpermissions`);
    }

    /**
     * Get permissions count grouped by regulation
     * @param businessUnit Optional business unit filter
     * @param dateRange Optional date range filter (e.g., 'This Week', 'This Month')
     */
    getPermissionsByRegulation(businessUnit?: string, dateRange?: string): Observable<PermissionsByRegulationItem[]> {
        const params: any = {};
        if (businessUnit && businessUnit !== 'all') {
            params.businessUnit = businessUnit;
        }
        if (dateRange) {
            params.dateRange = dateRange;
        }
        return this.httpClient.get<PermissionsByRegulationItem[]>(
            `${this.url}/preferencedashboard/permissionsbyregulation`,
            { params }
        );
    }

    /**
     * Get contact ingestion summary data
     * @param businessUnit Optional business unit filter
     * @param dateRange Optional date range filter
     */
    getContactIngestionSummary(businessUnit?: string, dateRange?: string): Observable<ContactIngestionSummary> {
        const params: any = {};
        if (businessUnit && businessUnit !== 'all') {
            params.businessUnit = businessUnit;
        }
        if (dateRange) {
            params.dateRange = dateRange;
        }
        return this.httpClient.get<ContactIngestionSummary>(
            `${this.url}/preferencedashboard/contactingestionsummary`,
            { params }
        );
    }
}